// kHtmlDomWriterCore - the WRITING half of kHtmlParserCore: turn a parsed IkDomElement tree back
// into markup. Pure TypeScript, no DOM, so it runs in Node / an Azure Function like the parser.
//
// WHY IT EXISTS (2026-09-26). The parser stores attribute names lower-case and tag names upper-case
// (the HTML rule, and what every consumer looks up). SVG is XML and case-sensitive: a hand-rolled
// re-serialisation of an inline <svg> wrote `viewbox`, `preserveaspectratio`, `<lineargradient>`,
// which a renderer ignores, so the diagram lost its scaling or its gradient silently. The parser now
// keeps the spelling as written (`attributeNames`, `sourceTagName`); this writer is the one place
// that knows when to use it: inside an <svg> (up to a <foreignObject>, whose children are HTML) the
// written spelling, elsewhere the lower-case HTML form a browser's serializer produces.
//
// A second consumer needs nothing but a parsed tree: `kHtmlDomWriterCore.OuterHtml(El)`.

import { getCoreUtils } from "./kCoreHandler";
import { kHtmlParserCore } from "./kHtmlParserCore";
import type { IkDomElement, IkDomNode } from "./kHtmlParserCore";
import { kHTMLCore } from "./kHTMLCore";

export class kHtmlDomWriterCore {

    /** The element and its subtree as markup. An <svg> (and anything under one) keeps its case. */
    public static OuterHtml = (El: IkDomElement): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            return kHtmlDomWriterCore._WriteElement(El, kHtmlDomWriterCore.IsInSvg(El.parentNode));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlDomWriterCore.OuterHtml");
            return "";
        }
    };

    /** The children of an element as markup (a parsed document's body: `InnerHtml(Doc.body)`). */
    public static InnerHtml = (El: IkDomElement): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            return kHtmlDomWriterCore._WriteChildren(El, kHtmlDomWriterCore._ChildrenInSvg(El, kHtmlDomWriterCore.IsInSvg(El.parentNode)));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlDomWriterCore.InnerHtml");
            return "";
        }
    };

    /** True when El's CHILDREN would be in the SVG namespace: El is an <svg> or inside one, and not
     *  inside a <foreignObject> nearer than that <svg>. Null (no element) is false. */
    public static IsInSvg = (El: IkDomElement | null): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            let Node: IkDomElement | null = El;
            while (Node !== null) {
                if (Node.tagName === "FOREIGNOBJECT") { return false; }
                if (Node.tagName === "SVG") { return true; }
                Node = Node.parentNode;
            }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlDomWriterCore.IsInSvg");
            return false;
        }
    };

    /** One element. `ParentInSvg` = the element sits in SVG content (its parent is svg or inside it). */
    private static _WriteElement = (El: IkDomElement, ParentInSvg: boolean): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const InSvg: boolean = ParentInSvg === true || El.tagName === "SVG";
            const Name: string = kHtmlDomWriterCore.TagNameOf(El, InSvg);
            const Open: string = "<" + Name + kHtmlDomWriterCore._WriteAttributes(El, InSvg);
            const Inner: string = kHtmlDomWriterCore._WriteChildren(El, kHtmlDomWriterCore._ChildrenInSvg(El, ParentInSvg));
            if (InSvg === false && kHtmlParserCore.IsVoidTag(El.tagName) === true) { return Open + ">"; }
            if (InSvg === true && Inner === "") { return Open + "/>"; }
            return Open + ">" + Inner + "</" + Name + ">";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlDomWriterCore._WriteElement");
            return "";
        }
    };

    /** Whether El's children are SVG content: El is svg or in svg, and El is not a foreignObject. */
    private static _ChildrenInSvg = (El: IkDomElement, ParentInSvg: boolean): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (El.tagName === "FOREIGNOBJECT") { return false; }
            return ParentInSvg === true || El.tagName === "SVG";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlDomWriterCore._ChildrenInSvg");
            return false;
        }
    };

    private static _WriteChildren = (El: IkDomElement, InSvg: boolean): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let Out: string = "";
            for (const Child of El.childNodes) { Out = Out + kHtmlDomWriterCore._WriteNode(Child, InSvg); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlDomWriterCore._WriteChildren");
            return "";
        }
    };

    private static _WriteNode = (Node: IkDomNode, InSvg: boolean): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Node.nodeType === 3) { return kHtmlDomWriterCore.EscapeText(Node.nodeValue || ""); }
            if (Node.nodeType !== 1) { return ""; }
            return kHtmlDomWriterCore._WriteElement(Node as IkDomElement, InSvg);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlDomWriterCore._WriteNode");
            return "";
        }
    };

    /** The written spelling in SVG content (InSvg true), lower-case HTML elsewhere. Shared with kHtmlSafeCore.TagOf (reuse #16321). */
    public static TagNameOf = (El: IkDomElement, InSvg: boolean): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const Written: string = El.sourceTagName || "";
            if (InSvg === true && Written !== "") { return Written; }
            return El.tagName.toLowerCase();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlDomWriterCore.TagNameOf");
            return "";
        }
    };

    /** ` name="value"` for every attribute, in the order written. */
    private static _WriteAttributes = (El: IkDomElement, InSvg: boolean): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let Out: string = "";
            for (const Key of Object.keys(El.attributes)) {
                let Name: string = Key;
                if (InSvg === true && El.attributeNames !== undefined && El.attributeNames[Key] !== undefined) {
                    Name = El.attributeNames[Key];
                }
                Out = Out + " " + Name + "=\"" + kHtmlDomWriterCore.EscapeAttribute(El.attributes[Key]) + "\"";
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlDomWriterCore._WriteAttributes");
            return "";
        }
    };

    /** Text content escaped for markup: & < > (the parser decoded them, so they are re-encoded). */
    public static EscapeText = (Text: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            return kHTMLCore.EscapeHtml(Text);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlDomWriterCore.EscapeText");
            return "";
        }
    };

    /** An attribute value escaped for a double-quoted attribute. */
    public static EscapeAttribute = (Value: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            return kHTMLCore.EscapeAttr(Value);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlDomWriterCore.EscapeAttribute");
            return "";
        }
    };
}
